/**
 * Four list-editing behaviours ProseMirror's defaults get wrong.
 *
 * 1. Backspace at the start of a list item. The default binding is
 *    `joinBackward`, which folds the item into the one above it as an extra
 *    paragraph: the marker disappears, the text stays indented under the
 *    previous item, and every later item renumbers. It reads as the item —
 *    and, on a short list, the whole list — having been swallowed, and the
 *    only way to get a plain paragraph back was to select the item and
 *    toggle the list off from the toolbar. Every document editor people
 *    arrive from (Docs, Word, Notion, Confluence) answers this key by
 *    removing the marker instead, so that is what it does here: the item's
 *    content leaves its list at the same position, splitting the list around
 *    it, and an ordered list's later half keeps counting from where it was.
 *    A nested item becomes a paragraph inside its parent item, which is the
 *    same "no marker, same indent" result one level down.
 *
 * 2. Backspace at the start of a paragraph that follows a list. The default
 *    wraps the paragraph back into the list as a new item — so after (1),
 *    a second Backspace re-added the marker it had just removed, and holding
 *    the key toggled the item in and out of the list forever instead of
 *    joining its text onto the line above. Here the paragraph's text joins
 *    the end of the list's last line, which is what the second press does
 *    in every editor above.
 *
 * 3. Typing a list marker at the start of an item in the OTHER kind of list.
 *    The stock `- ` and `1. ` input rules wrap a paragraph in a new list,
 *    which is impossible for the paragraph a list item starts with, so they
 *    silently did nothing and the only way to put bullets under a numbered
 *    item (or numbers under a bullet) was the toolbar. Here the item is
 *    moved into a list of the typed kind in place — the list is split
 *    around it, and it merges into an adjacent list of that kind when there
 *    is one, so typing `- ` on several consecutive items builds one list.
 *
 * 4. Starting a list on a line that follows a soft break (Shift+Enter).
 *    A soft break keeps both lines in ONE paragraph, and in chat — where
 *    Enter sends — it is the only way to start a new line, so it is how
 *    every multi-line message is written. The list button wrapped the whole
 *    paragraph, pulling the lines above into the first item, and `- ` / `1. `
 *    typed after the break did nothing because the rules only look at the
 *    start of a paragraph. Here both first split the soft-broken lines being
 *    listed off into paragraphs of their own, so the list starts on the line
 *    it was started on. A selection spanning several soft-broken lines gets
 *    one item per line.
 *
 * Task items are deliberately untouched: their checkbox is the item, not a
 * marker to strip.
 */

import type { NodeType, Node as PMNode, ResolvedPos } from '@tiptap/pm/model'
import { Fragment } from '@tiptap/pm/model'
import { TextSelection, type Transaction } from '@tiptap/pm/state'
import { canJoin, canSplit, findWrapping } from '@tiptap/pm/transform'
import { type Editor, Extension, InputRule } from '@tiptap/react'

const LIST_TYPES = new Set(['bulletList', 'orderedList'])

interface ItemContext {
  list: PMNode
  listPos: number
  /** The list's own parent, which receives the replacement. */
  parent: PMNode
  /** The list's index within `parent`. */
  indexInParent: number
  /** The item's index within `list`. */
  itemIndex: number
}

/**
 * The list item the caret's textblock opens, or null when the textblock is
 * not the first child of a `listItem` in a bullet or ordered list.
 */
function itemAtBlockStart($pos: ResolvedPos): ItemContext | null {
  const blockDepth = $pos.depth
  if (blockDepth < 3) return null
  const item = $pos.node(blockDepth - 1)
  if (item.type.name !== 'listItem' || $pos.index(blockDepth - 1) !== 0) return null
  const listDepth = blockDepth - 2
  const list = $pos.node(listDepth)
  if (!LIST_TYPES.has(list.type.name)) return null
  return {
    list,
    listPos: $pos.before(listDepth),
    parent: $pos.node(listDepth - 1),
    indexInParent: $pos.index(listDepth - 1),
    itemIndex: $pos.index(listDepth),
  }
}

function childrenOf(node: PMNode): PMNode[] {
  return Array.from({ length: node.childCount }, (_, index) => node.child(index))
}

function startOf(list: PMNode): number {
  const start = list.attrs.start
  return typeof start === 'number' ? start : 1
}

/**
 * The items before and after the target, each as a list of the original
 * kind. An ordered list's second half starts where its numbering would have
 * continued had the target never been in it.
 */
function splitAround(ctx: ItemContext): { before: PMNode | null; after: PMNode | null } {
  const { list, itemIndex } = ctx
  const items = childrenOf(list)
  const beforeItems = items.slice(0, itemIndex)
  const afterItems = items.slice(itemIndex + 1)
  const afterAttrs =
    list.type.name === 'orderedList'
      ? { ...list.attrs, start: startOf(list) + itemIndex }
      : list.attrs
  return {
    before: beforeItems.length > 0 ? list.type.create(list.attrs, beforeItems) : null,
    after: afterItems.length > 0 ? list.type.create(afterAttrs, afterItems) : null,
  }
}

/**
 * Take the item at the caret out of its list, leaving its content where it
 * was. Returns false (changing nothing) when the result would not fit the
 * parent's content model.
 */
export function unwrapListItemAt(tr: Transaction, pos: number): boolean {
  const ctx = itemAtBlockStart(tr.doc.resolve(pos))
  if (!ctx) return false
  const item = ctx.list.child(ctx.itemIndex)
  const { before, after } = splitAround(ctx)

  const pieces = [before, ...childrenOf(item), after].filter(
    (node): node is PMNode => node !== null,
  )
  const replacement = Fragment.from(pieces)
  if (!ctx.parent.canReplace(ctx.indexInParent, ctx.indexInParent + 1, replacement)) return false

  tr.replaceWith(ctx.listPos, ctx.listPos + ctx.list.nodeSize, replacement)
  const blockStart = ctx.listPos + (before ? before.nodeSize : 0) + 1
  tr.setSelection(TextSelection.create(tr.doc, blockStart))
  return true
}

/**
 * Move the item at the caret into a list of `targetType`, splitting its
 * current list around it and merging with a neighbouring `targetType` list.
 */
export function retypeListItemAt(
  tr: Transaction,
  pos: number,
  targetType: NodeType,
  targetAttrs: Record<string, unknown> = {},
): boolean {
  const $pos = tr.doc.resolve(pos)
  const ctx = itemAtBlockStart($pos)
  if (!ctx || ctx.list.type === targetType) return false
  const offsetInBlock = $pos.parentOffset
  const item = ctx.list.child(ctx.itemIndex)
  const { before, after } = splitAround(ctx)
  const retyped = targetType.create(targetAttrs, item)

  const pieces = [before, retyped, after].filter((node): node is PMNode => node !== null)
  const replacement = Fragment.from(pieces)
  if (!ctx.parent.canReplace(ctx.indexInParent, ctx.indexInParent + 1, replacement)) return false

  tr.replaceWith(ctx.listPos, ctx.listPos + ctx.list.nodeSize, replacement)
  const retypedPos = ctx.listPos + (before ? before.nodeSize : 0)
  // list > item > block, then the caret's offset inside the block.
  const caret = retypedPos + 3 + offsetInBlock
  const joinsFrom = tr.steps.length

  // The trailing boundary first, so the leading boundary's position is
  // still valid when it is joined.
  const retypedEnd = retypedPos + retyped.nodeSize
  if (!after && canJoin(tr.doc, retypedEnd)) {
    const next = tr.doc.resolve(retypedEnd).nodeAfter
    if (next?.type === targetType) tr.join(retypedEnd)
  }
  if (!before && canJoin(tr.doc, retypedPos)) {
    const previous = tr.doc.resolve(retypedPos).nodeBefore
    if (previous?.type === targetType) tr.join(retypedPos)
  }

  const mapped = tr.mapping.slice(joinsFrom).map(caret)
  tr.setSelection(TextSelection.create(tr.doc, mapped))
  return true
}

/**
 * Join the paragraph at the caret onto the end of the last line of the list
 * directly before it. Returns false when the caret is not at the start of a
 * paragraph that follows a list, or when the text can't join that line.
 */
export function joinParagraphIntoPreviousList(tr: Transaction, pos: number): boolean {
  const $pos = tr.doc.resolve(pos)
  const block = $pos.parent
  if (block.type.name !== 'paragraph' || $pos.parentOffset !== 0 || $pos.depth < 1) return false
  const blockPos = $pos.before()
  const previous = tr.doc.resolve(blockPos).nodeBefore
  if (!previous || !LIST_TYPES.has(previous.type.name)) return false

  // Walk down the list's last descendants to the textblock the eye reads as
  // "the line above".
  let target: PMNode = previous
  let targetEnd = blockPos
  while (!target.isTextblock) {
    const last = target.lastChild
    if (!last) return false
    targetEnd -= 1
    target = last
  }
  // `targetEnd` is now just past the target's closing token.
  const insertAt = targetEnd - 1
  if (!target.type.validContent(target.content.append(block.content))) return false

  tr.delete(blockPos, blockPos + block.nodeSize)
  tr.insert(insertAt, block.content)
  tr.setSelection(TextSelection.create(tr.doc, insertAt))
  return true
}

type ListName = 'bulletList' | 'orderedList'

/** Positions of the soft breaks directly inside the textblock `$pos` sits in. */
function softBreakPositions($pos: ResolvedPos): number[] {
  const block = $pos.parent
  const positions: number[] = []
  let offset = $pos.start()
  for (let index = 0; index < block.childCount; index += 1) {
    const child = block.child(index)
    if (child.type.name === 'hardBreak') positions.push(offset)
    offset += child.nodeSize
  }
  return positions
}

/** Replace the soft break at `pos` with a paragraph boundary. */
function splitAtSoftBreak(tr: Transaction, pos: number): boolean {
  if (!canSplit(tr.doc, pos + 1)) return false
  tr.delete(pos, pos + 1)
  tr.split(pos)
  return true
}

/**
 * Split the selection's soft-broken lines off into paragraphs of their own:
 * at the break before the first selected line, between selected lines, and
 * at the break after the last one. A no-op outside a single paragraph.
 */
export function isolateSelectedSoftLines(tr: Transaction): void {
  const { $from, $to } = tr.selection
  if (!$from.sameParent($to) || $from.parent.type.name !== 'paragraph') return
  const breaks = softBreakPositions($from)
  if (breaks.length === 0) return
  const { pos: from } = $from
  const { pos: to } = $to
  const cuts = [
    breaks.filter((pos) => pos < from).at(-1),
    ...breaks.filter((pos) => pos >= from && pos < to),
    breaks.find((pos) => pos >= to),
  ].filter((pos): pos is number => pos !== undefined)
  // Last first, so every earlier position is still valid when it is cut.
  for (const pos of [...new Set(cuts)].sort((a, b) => b - a)) splitAtSoftBreak(tr, pos)
}

/**
 * The list button and its shortcut. Turning a list ON first isolates the
 * selected soft-broken lines; turning one off, or switching kinds, is the
 * stock toggle.
 */
export function toggleListByLine(editor: Editor, list: ListName): boolean {
  const chain = editor.chain().focus()
  if (!editor.isActive(list)) {
    chain.command(({ tr }) => {
      isolateSelectedSoftLines(tr)
      return true
    })
  }
  return list === 'bulletList' ? chain.toggleBulletList().run() : chain.toggleOrderedList().run()
}

/**
 * Turn the line that starts after the soft break at `breakPos` into the only
 * item of a new list. The caller has already removed the typed marker.
 */
function listLineAfterSoftBreak(
  tr: Transaction,
  breakPos: number,
  listType: NodeType,
  attrs: Record<string, unknown> = {},
): boolean {
  if (!splitAtSoftBreak(tr, breakPos)) return false
  // Past the first paragraph's closing token and the new one's opening.
  const range = tr.doc.resolve(breakPos + 2).blockRange()
  const wrapping = range && findWrapping(range, listType, attrs)
  if (!range || !wrapping) return false
  tr.wrap(range, wrapping)
  return true
}

/** The soft break the typed marker at `from` follows directly, if any. */
function softBreakBefore(tr: Transaction, from: number): number | null {
  const $from = tr.doc.resolve(from)
  if ($from.parent.type.name !== 'paragraph') return null
  return $from.nodeBefore?.type.name === 'hardBreak' ? from - 1 : null
}

export const ListItemEditing = Extension.create({
  name: 'listItemEditing',

  // Ahead of the list extensions (100): their wrapping input rules and the
  // core Backspace binding would otherwise claim these keystrokes first.
  priority: 150,

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { state, view } = this.editor
        const { selection } = state
        if (!(selection instanceof TextSelection) || !selection.empty) return false
        const $cursor = selection.$cursor
        if (!$cursor || $cursor.parentOffset !== 0) return false
        const tr = state.tr
        const handled = itemAtBlockStart($cursor)
          ? unwrapListItemAt(tr, $cursor.pos)
          : joinParagraphIntoPreviousList(tr, $cursor.pos)
        if (!handled) return false
        // Something an input rule JUST produced goes back to the typed
        // characters first, as it does everywhere else in the editor.
        if (this.editor.commands.undoInputRule()) return true
        view.dispatch(tr.scrollIntoView())
        return true
      },
      'Mod-Shift-8': () => toggleListByLine(this.editor, 'bulletList'),
      'Mod-Shift-7': () => toggleListByLine(this.editor, 'orderedList'),
    }
  },

  addInputRules() {
    const { bulletList, orderedList } = this.editor.schema.nodes
    const rules: InputRule[] = []
    if (bulletList) {
      rules.push(
        new InputRule({
          find: /([-+*])\s$/,
          handler: ({ state, range }) => {
            const tr = state.tr
            const breakPos = softBreakBefore(tr, range.from)
            if (breakPos === null) return null
            tr.delete(range.from, range.to)
            if (!listLineAfterSoftBreak(tr, breakPos, bulletList)) return null
          },
        }),
      )
      rules.push(
        new InputRule({
          find: /^\s*([-+*])\s$/,
          handler: ({ state, range }) => {
            const tr = state.tr
            const ctx = itemAtBlockStart(tr.doc.resolve(range.from))
            if (!ctx || ctx.list.type === bulletList) return null
            tr.delete(range.from, range.to)
            if (!retypeListItemAt(tr, range.from, bulletList)) return null
          },
        }),
      )
    }
    if (orderedList) {
      rules.push(
        new InputRule({
          find: /(\d+)\.\s$/,
          handler: ({ state, range, match }) => {
            const tr = state.tr
            const breakPos = softBreakBefore(tr, range.from)
            if (breakPos === null) return null
            tr.delete(range.from, range.to)
            const start = Number.parseInt(match[1] ?? '1', 10)
            if (!listLineAfterSoftBreak(tr, breakPos, orderedList, { start })) return null
          },
        }),
      )
      rules.push(
        new InputRule({
          find: /^(\d+)\.\s$/,
          handler: ({ state, range, match }) => {
            const tr = state.tr
            const ctx = itemAtBlockStart(tr.doc.resolve(range.from))
            if (!ctx || ctx.list.type === orderedList) return null
            tr.delete(range.from, range.to)
            const start = Number.parseInt(match[1] ?? '1', 10)
            if (!retypeListItemAt(tr, range.from, orderedList, { start })) return null
          },
        }),
      )
    }
    return rules
  },
})
